import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../app/store';
import { useAppSelector } from '../app/hooks';
import { useApplyPromoCodeMutation, useRemovePromoCodeMutation, useGetPromoCodeQuery, PromoAppliedResult } from '../features/api/api';
import { setAppliedPromo } from '../features/settings/settings-slice';
import { isFetchBaseQueryError, isErrorWithMessage, UnwrapError } from '../helpers/error-typing';
import { setIsLoadingStatus } from '../features/calendar/calendar-slice';

export const Promo = () => {
    const dispatch = useAppDispatch();
    const appliedPromo = useAppSelector(state => state.settings.promoCode);
    const appliedPromoDescription = useAppSelector(state => state.settings.promoCodeDescription);
    const { promoCodeDescriptionText, applyPromoCodeButtonText, clearAllButtonText, appliedPromoCodeHeading, removePromoCodeButtonText } = useAppSelector(state => state.settings);
    const inputRef = useRef<HTMLInputElement>(null);
    const promoInputHiddenText = useAppSelector(state => state.settings.promoInputHiddenText);
    const hasPerformanceInCart = useAppSelector(state => state.settings.hasPerformanceInCart);

    const [showPromo, setShowPromo] = useState(!!appliedPromo);
    const [promoValue, setPromoValue] = useState(appliedPromo || "");
    const [errorMessage, setErrorMessage] = useState("");

    const [applyPromo, { data: appliedResult, isLoading: isApplying, isError, isSuccess: hasApplied }] = useApplyPromoCodeMutation();
    const [removePromo, { isLoading: isRemoving }] = useRemovePromoCodeMutation();

    const onFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setPromoValue(inputRef.current?.value || "");
    }

    useEffect(() => {
        if (!!promoValue && !appliedPromo) handleApply();
    }, [promoValue]);

    const handleApply = async () => {
        dispatch(setIsLoadingStatus(true));
        await applyPromo(promoValue).unwrap().then((fulfilled: any) => {
            dispatch(setAppliedPromo(promoValue));
            clearErrors();
            dispatch(setIsLoadingStatus(false));
        }).catch((rejected: UnwrapError) => {
            let errorMessage = '';
            if (isFetchBaseQueryError(rejected)) {
                if ('data' in rejected) {
                    errorMessage = typeof (rejected.data) == 'string' ? rejected.data : JSON.stringify(rejected.data);
                }
            } else if (isErrorWithMessage(rejected)) {
                errorMessage = rejected.message;
            }
            setErrorMessage(errorMessage);
            dispatch(setIsLoadingStatus(false));
        })
    }

    const handleRemove = async () => {
        dispatch(setIsLoadingStatus(true));
        setPromoValue('');
        await removePromo().unwrap().then((fulfilled: any) => {
            setPromoValue("");
            dispatch(setAppliedPromo(""));
            clearErrors();
            dispatch(setIsLoadingStatus(false));
        }).catch((rejected: UnwrapError) => {
            let errorMessage = '';
            if (isFetchBaseQueryError(rejected)) {
                if ('data' in rejected) {
                    errorMessage = typeof (rejected.data) == 'string' ? rejected.data : JSON.stringify(rejected.data);
                }
            } else if (isErrorWithMessage(rejected)) {
                errorMessage = rejected.message;
            }
            setErrorMessage(errorMessage);
            dispatch(setIsLoadingStatus(false));
        })
    }

    const clearErrors = () => {
        setErrorMessage("");
    }

    const clearInput = () => {
        if (inputRef.current) {
            inputRef.current.value = "";
        }
        setPromoValue("");
        setErrorMessage("");
    }

    return (
        <div className="ace-cal-promo-container">
            {
                !showPromo ?
                    <>
                        {hasPerformanceInCart && <p className="italic">{promoInputHiddenText}</p>}
                        {!hasPerformanceInCart &&
                            <button
                                className="btn-secondary"
                                data-testid="promoCode show"
                                onClick={() => setShowPromo(true)}
                                disabled={hasPerformanceInCart}
                            >
                                {promoCodeDescriptionText}
                            </button>}

                    </>
                    :
                    !appliedPromo ?
                        <form onSubmit={(e: React.FormEvent) => onFormSubmit(e)} className="stack-xs" data-testid="promoCode form">
                            <label htmlFor="promo_input">{promoCodeDescriptionText}</label>
                            <div className="inline-input-form">
                                <div className="inline-input-clear">
                                    <input type="text" id="promo_input" ref={inputRef} onChange={() => clearErrors()} className={!!inputRef.current?.value ? "has-x-btn" : ""} />
                                    {
                                        !!inputRef.current?.value &&
                                        <button onClick={() => clearInput()} className="x-btn" data-testid="promoCode clear"><span className="visually-hidden">
                                            {clearAllButtonText}
                                        </span></button>
                                    }
                                </div>
                                <button type="submit" className="btn">{applyPromoCodeButtonText}</button>
                            </div>
                            {
                                isError && <p className="error-message" data-testid="promoCodeErrorMessage">{errorMessage}</p>
                            }
                        </form>
                        :
                        <div className="flex-between">
                            <div>
                                <p className="accent">{appliedPromoCodeHeading} {appliedResult?.webPromoCode?.promoCode || appliedPromo}</p>
                                <p
                                    className="bold"
                                    dangerouslySetInnerHTML={{
                                        __html: appliedResult?.webPromoCode.text2 || appliedPromoDescription,
                                    }}
                                />
                            </div>
                            <button onClick={() => handleRemove()} data-testid="promoCode remove" className="btn-primary btn-remove">{removePromoCodeButtonText}</button>
                            {
                                isError && <p className="error-message" data-testid="promoCodeErrorMessage">{errorMessage}</p>
                            }
                        </div>
            }
        </div>
    )
}